import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NotificationService } from '../../services/notification.service';
import { concat, EMPTY, Observable } from 'rxjs';
import { catchError, filter, last, switchMap, withLatestFrom } from 'rxjs/operators';

import {
  AddSignatureMatrixTemplate,
  AddUnsignedSignatureMatrices,
  CertificateActionTypes,
  CertificateDetailsPatch,
  CertificateSignatureMatrixSave,
  CloneSignatureMatrixTemplates,
  DeleteSignatureMatrix,
  DeleteSignatureMatrixTemplate,
  FetchDefaultSignatureMatrices,
  FetchDefaultSignatureMatricesSuccess,
  HandoverPatch,
  HandoverSignatureMatrixSave,
  InsertAndSignSignatureMatrix,
  SignatureMatrixActionTypes,
  SigntureMatrixTemplateLoadSuccess,
  ToggleSignSignatureMatrix,
  UpdateSignatureMatrixTemplate,
} from '@completion/actions';
import { State } from '@completion/reducers';
import { getCurrentCpId, getCurrentProject } from '@completion/selectors';
import { SignatureMatrixService } from '@completion/services';
import { Project } from '@completion/models';
import { trackResource } from './resource-fetch.util';

@Injectable()
export class SignatureMatrixEffect {
  constructor(
    private readonly actions$: Actions,
    private readonly signatureMatrixService: SignatureMatrixService,
    private readonly store: Store<State>,
    private readonly toastr: NotificationService,
  ) {}

  certificateSaveSignatureMatrix$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CertificateSignatureMatrixSave>(SignatureMatrixActionTypes.CertificateSignatureMatrixSave),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      filter(([_, currentProject, cpId]) => !!(currentProject && cpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.signatureMatrixService,
        method: 'certificateSaveSignatureMatrix',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.certType, action.certId, action.matrix],
        onSuccess: (response) => new CertificateDetailsPatch(response as any),
      }),
    ),
  );

  handoverSaveSignatureMatrix$ = createEffect(() =>
    this.actions$.pipe(
      ofType<HandoverSignatureMatrixSave>(SignatureMatrixActionTypes.HandoverSignatureMatrixSave),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>, this.store.select(getCurrentCpId) as Observable<number>),
      filter(([_, currentProject, cpId]) => !!(currentProject && cpId)),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.signatureMatrixService,
        method: 'handoverSaveSignatureMatrix',
        serviceArgs: ([action, currentProject, cpId]) => [currentProject.id, cpId, action.handoverId, action.matrix],
        onSuccess: (response) => new HandoverPatch(response as any),
      }),
    ),
  );

  fetchDefaultSignatureMatrices$ = createEffect(() =>
    this.actions$.pipe(
      ofType<FetchDefaultSignatureMatrices>(SignatureMatrixActionTypes.FetchDefaultSignatureMatrices),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.signatureMatrixService,
        method: 'fetchDefaultSignatureMatrices',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (response) => new FetchDefaultSignatureMatricesSuccess(response as any),
      }),
    ),
  );

  deleteSignatureMatrixTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteSignatureMatrixTemplate>(SignatureMatrixActionTypes.DeleteSignatureMatrixTemplate),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.signatureMatrixService,
        method: 'deleteSignatureMatrixTemplate',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.matrix.id],
        onSuccess: (response) => new SigntureMatrixTemplateLoadSuccess(response as any),
      }),
    ),
  );

  updateSignatureMatrixTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<UpdateSignatureMatrixTemplate>(SignatureMatrixActionTypes.UpdateSignatureMatrixTemplate),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.signatureMatrixService,
        method: 'updateSignatureMatrixTemplate',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.matrix],
        onSuccess: (response) => new SigntureMatrixTemplateLoadSuccess(response as any),
      }),
    ),
  );

  addSignatureMatrixTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AddSignatureMatrixTemplate>(SignatureMatrixActionTypes.AddSignatureMatrixTemplate),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.signatureMatrixService,
        method: 'addSignatureMatrixTemplate',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.matrix],
        onSuccess: (response) => new SigntureMatrixTemplateLoadSuccess(response as any),
      }),
    ),
  );

  addUnsignedSignatureMatrices$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AddUnsignedSignatureMatrices>(SignatureMatrixActionTypes.AddUnsignedSignatureMatrices),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.signatureMatrixService,
        method: 'addUnsignedSignatureMatrices',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.matrix],
        onSuccess: () => null,
      }),
    ),
  );

  saveSignatureMatrix$ = createEffect(() =>
    this.actions$.pipe(
      ofType<AddUnsignedSignatureMatrices>(SignatureMatrixActionTypes.SaveSignatureMatrix),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.signatureMatrixService,
        method: 'saveSignatureMatrix',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.matrix],
        onSuccess: () => null,
      }),
    ),
  );

  deleteSignatureMatrix$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteSignatureMatrix>(SignatureMatrixActionTypes.DeleteSignatureMatrix),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.signatureMatrixService,
        method: 'deleteSignatureMatrix',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.matrixId],
        onSuccess: () => null,
      }),
    ),
  );

  insertAndSignCertificateSignatureMatrix$ = createEffect(() =>
    this.actions$.pipe(
      ofType<InsertAndSignSignatureMatrix>(CertificateActionTypes.InsertAndSignSignatureMatrix),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.signatureMatrixService,
        method: 'insertAndSignSignatureMatrix',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.parentId, [action.entry]],
        onSuccess: () => null,
      }),
    ),
  );

  toggleSignSignatureMatrix$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ToggleSignSignatureMatrix>(SignatureMatrixActionTypes.ToggleSignSignatureMatrix),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.signatureMatrixService,
        method: 'toggleSignSignatureMatrix',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.matrixId],
        onSuccess: () => null,
      }),
    ),
  );

  cloneSignatureMatrixTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CloneSignatureMatrixTemplates>(SignatureMatrixActionTypes.CloneSignatureMatrixTemplates),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      filter(([_, currentProject]) => !!currentProject),
      switchMap(([action, currentProject]) => {
        const requests = action.templates.map((template) =>
          this.signatureMatrixService.addSignatureMatrixTemplate(currentProject.id, template),
        );
        return concat(...requests).pipe(
          last(),
          switchMap((finalResult) => [new SigntureMatrixTemplateLoadSuccess(finalResult as any)]),
          catchError((err) => {
            this.toastr.error(err?.error?.message || 'Failed to clone templates');
            return EMPTY;
          }),
        );
      }),
    ),
  );
}
